/**
 * CDM Enforcement Center — Track 2.7.
 *
 * Bulk-fires an Action across every row a saved query returns.
 * Two-pane layout: left = launcher form (saved-query + action picker,
 * dry-run toggle, abort threshold); right = past runs with a results
 * pie per card. Clicking a card opens a drawer with the per-target
 * outcome list pulled fresh from /api/cdm/enforcement/{id}.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Zap, Play, X, RefreshCw, AlertTriangle, CheckCircle2, SkipForward, Ban, Loader2 } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface SavedQuery {
  id: string
  name: string
  display_name: string
  archetype: string
}

interface ActionRow {
  id: number
  identifier: string
  title: string
  handler_type: string
}

interface InvocationCounts {
  ok: number
  failed: number
  skipped: number
  total: number
}

interface InvocationListItem {
  id: string
  saved_query_id: string
  action_id: number
  target_count: number
  started_by: string
  started_at: string | null
  completed_at: string | null
  dry_run: boolean
  abort_on_failure_after: number | null
  status: 'queued' | 'running' | 'completed' | 'aborted' | 'failed'
  counts: InvocationCounts
}

interface InvocationResult {
  target_id: string
  ok: boolean
  status_code?: number | null
  detail?: string
  skipped?: boolean
  fired_at?: string
}

interface InvocationDetail extends InvocationListItem {
  results: InvocationResult[]
  saved_query_name?: string
  action_title?: string
  action_identifier?: string
}

const STATUS_TONE: Record<string, string> = {
  queued: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  running: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  completed: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  aborted: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  failed: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
}

function elapsed(started: string | null, completed: string | null): string {
  if (!started) return '—'
  const a = Date.parse(started)
  const b = completed ? Date.parse(completed) : Date.now()
  if (Number.isNaN(a) || Number.isNaN(b)) return '—'
  const sec = Math.max(0, Math.floor((b - a) / 1000))
  if (sec < 60) return `${sec}s`
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`
  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`
}

function ResultsPie({ counts }: { counts: InvocationCounts }) {
  const total = counts.total || 1
  const okPct = (counts.ok / total) * 100
  const failPct = (counts.failed / total) * 100
  const skipPct = (counts.skipped / total) * 100
  return (
    <div className="flex items-center gap-3">
      <div
        className="w-12 h-12 rounded-full border border-white/10"
        style={{
          background: counts.total === 0
            ? 'rgba(255,255,255,0.05)'
            : `conic-gradient(
                rgb(16 185 129) 0 ${okPct}%,
                rgb(244 63 94) ${okPct}% ${okPct + failPct}%,
                rgb(148 163 184) ${okPct + failPct}% ${okPct + failPct + skipPct}%,
                rgba(255,255,255,0.05) ${okPct + failPct + skipPct}% 100%
              )`,
        }}
        title={`${counts.ok} ok · ${counts.failed} failed · ${counts.skipped} skipped`}
      />
      <div className="text-xs space-y-0.5 leading-tight">
        <div className="flex items-center gap-1 text-emerald-300"><CheckCircle2 className="w-3 h-3" /> {counts.ok} ok</div>
        <div className="flex items-center gap-1 text-rose-300"><AlertTriangle className="w-3 h-3" /> {counts.failed} failed</div>
        <div className="flex items-center gap-1 text-text-muted"><SkipForward className="w-3 h-3" /> {counts.skipped} skip</div>
      </div>
    </div>
  )
}

export default function CdmEnforcementPage() {
  const [queries, setQueries] = useState<SavedQuery[]>([])
  const [actions, setActions] = useState<ActionRow[]>([])
  const [invocations, setInvocations] = useState<InvocationListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [drawer, setDrawer] = useState<InvocationDetail | null>(null)

  // launcher form state
  const [queryId, setQueryId] = useState('')
  const [actionId, setActionId] = useState<number | ''>('')
  const [dryRun, setDryRun] = useState(true)
  const [abortAfter, setAbortAfter] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState('')

  const queryById = useMemo(() => Object.fromEntries(queries.map(q => [q.id, q])), [queries])
  const actionById = useMemo(() => Object.fromEntries(actions.map(a => [a.id, a])), [actions])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [q, a, inv] = await Promise.all([
        api.get('/api/cdm/queries'),
        api.get('/api/actions'),
        api.get('/api/cdm/enforcement'),
      ])
      setQueries((q.data?.queries || []) as SavedQuery[])
      setActions((a.data?.actions || []) as ActionRow[])
      setInvocations((inv.data?.invocations || []) as InvocationListItem[])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  // Auto-refresh while there's an in-flight run
  useEffect(() => {
    const live = invocations.some(i => i.status === 'queued' || i.status === 'running')
    if (!live) return
    const t = setInterval(() => { void load() }, 3000)
    return () => clearInterval(t)
  }, [invocations, load])

  const openDrawer = useCallback(async (id: string) => {
    try {
      const r = await api.get(`/api/cdm/enforcement/${id}`)
      setDrawer(r.data as InvocationDetail)
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } }
      setMessage(err.response?.data?.detail || 'Failed to load invocation detail')
    }
  }, [])

  const refreshDrawer = useCallback(async () => {
    if (!drawer) return
    try {
      const r = await api.get(`/api/cdm/enforcement/${drawer.id}`)
      setDrawer(r.data as InvocationDetail)
    } catch { /* ignore */ }
  }, [drawer])

  // Auto-poll the drawer while its invocation is live
  useEffect(() => {
    if (!drawer) return
    if (drawer.status !== 'queued' && drawer.status !== 'running') return
    const t = setInterval(() => { void refreshDrawer() }, 2000)
    return () => clearInterval(t)
  }, [drawer, refreshDrawer])

  const submit = useCallback(async () => {
    setMessage('')
    if (!queryId || !actionId) {
      setMessage('Pick a saved query and an action')
      return
    }
    setSubmitting(true)
    try {
      const body: Record<string, unknown> = {
        saved_query_id: queryId,
        action_id: actionId,
        dry_run: dryRun,
      }
      if (abortAfter.trim()) {
        const n = parseInt(abortAfter.trim(), 10)
        if (Number.isFinite(n) && n > 0) body.abort_on_failure_after = n
      }
      const r = await api.post('/api/cdm/enforcement', body)
      setMessage(`Queued — ${r.data?.id || 'invocation'}`)
      await load()
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } }
      setMessage(err.response?.data?.detail || 'Failed to launch run')
    } finally {
      setSubmitting(false)
    }
  }, [queryId, actionId, dryRun, abortAfter, load])

  const abort = useCallback(async (id: string) => {
    try {
      await api.post(`/api/cdm/enforcement/${id}/abort`)
      await load()
      if (drawer?.id === id) await refreshDrawer()
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } }
      setMessage(err.response?.data?.detail || 'Abort failed')
    }
  }, [load, drawer, refreshDrawer])

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Zap className="w-6 h-6 text-amber-300" />
            Enforcement Center
          </h1>
          <p className="text-sm text-text-muted mt-1 max-w-2xl">
            Pick a saved query, pick an action, fan it out. Use dry-run to
            preview the blast radius and the abort gate to stop a misfire
            before it eats the whole org.
          </p>
        </div>
        <button
          onClick={() => void load()}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-white/10 rounded-lg hover:bg-white/5"
        >
          <RefreshCw className={clsx('w-4 h-4', loading && 'animate-spin')} />
          Refresh
        </button>
      </header>

      {message && (
        <div className="px-3 py-2 text-sm border border-amber-500/30 bg-amber-500/10 rounded-lg text-amber-200">
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Launcher */}
        <section className="lg:col-span-2 border border-white/10 rounded-xl p-4 bg-white/[0.02] space-y-4">
          <h2 className="text-sm font-medium text-text-muted uppercase tracking-wider">Run new</h2>

          <div className="space-y-1">
            <label className="text-xs text-text-muted">Saved query</label>
            <select
              value={queryId}
              onChange={e => setQueryId(e.target.value)}
              className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">— choose —</option>
              {queries.map(q => (
                <option key={q.id} value={q.id}>
                  {q.display_name} ({q.archetype})
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-text-muted">Action</label>
            <select
              value={actionId}
              onChange={e => setActionId(e.target.value ? Number(e.target.value) : '')}
              className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">— choose —</option>
              {actions.map(a => (
                <option key={a.id} value={a.id}>
                  {a.title} [{a.handler_type}]
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-text-muted">Abort after N failures (blank = never)</label>
            <input
              type="number"
              min={1}
              value={abortAfter}
              onChange={e => setAbortAfter(e.target.value)}
              placeholder="e.g. 5"
              className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} />
            <span>Dry run — record targets, don't actually fire</span>
          </label>

          <button
            disabled={submitting || !queryId || !actionId}
            onClick={() => void submit()}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {dryRun ? 'Preview run' : 'Launch run'}
          </button>
        </section>

        {/* Past runs */}
        <section className="lg:col-span-3 space-y-3">
          <h2 className="text-sm font-medium text-text-muted uppercase tracking-wider">Past runs</h2>
          {invocations.length === 0 && (
            <div className="text-sm text-text-muted border border-dashed border-white/10 rounded-xl p-6 text-center">
              No runs yet — kick one off on the left.
            </div>
          )}
          {invocations.map(inv => {
            const sq = queryById[inv.saved_query_id]
            const a = actionById[inv.action_id]
            return (
              <button
                key={inv.id}
                onClick={() => void openDrawer(inv.id)}
                className="w-full text-left border border-white/10 rounded-xl p-4 bg-white/[0.02] hover:bg-white/[0.04] transition"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      {sq?.display_name || '(deleted query)'} <span className="text-text-muted">→</span> {a?.title || '(deleted action)'}
                    </div>
                    <div className="text-xs text-text-muted mt-1 flex items-center gap-2 flex-wrap">
                      <span className={clsx('px-1.5 py-0.5 rounded border text-[11px]', STATUS_TONE[inv.status] || 'bg-white/5 border-white/10')}>{inv.status}</span>
                      {inv.dry_run && <span className="px-1.5 py-0.5 rounded border border-white/10 text-[11px]">dry-run</span>}
                      <span>{inv.target_count} targets</span>
                      <span>elapsed {elapsed(inv.started_at, inv.completed_at)}</span>
                      <span>by {inv.started_by}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <ResultsPie counts={inv.counts} />
                    {(inv.status === 'queued' || inv.status === 'running') && (
                      <button
                        onClick={(e) => { e.stopPropagation(); void abort(inv.id) }}
                        className="p-2 rounded-lg border border-rose-500/30 hover:bg-rose-500/10 text-rose-300"
                        title="Abort run"
                      >
                        <Ban className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </button>
            )
          })}
        </section>
      </div>

      {/* Drawer */}
      {drawer && (
        <div className="fixed inset-0 z-40 flex justify-end" onClick={() => setDrawer(null)}>
          <div className="absolute inset-0 bg-black/60" />
          <div
            className="relative w-full max-w-xl h-full bg-[#0e1018] border-l border-white/10 overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-[#0e1018]/95 backdrop-blur border-b border-white/10 px-4 py-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{drawer.saved_query_name || '(query)'}</div>
                <div className="text-xs text-text-muted truncate">→ {drawer.action_title || '(action)'} · {drawer.target_count} targets</div>
              </div>
              <button onClick={() => setDrawer(null)} className="p-1 rounded hover:bg-white/5"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-xs text-text-muted flex items-center gap-2 flex-wrap">
                <span className={clsx('px-1.5 py-0.5 rounded border text-[11px]', STATUS_TONE[drawer.status] || 'bg-white/5 border-white/10')}>{drawer.status}</span>
                {drawer.dry_run && <span className="px-1.5 py-0.5 rounded border border-white/10 text-[11px]">dry-run</span>}
                {drawer.abort_on_failure_after != null && <span>abort after {drawer.abort_on_failure_after} failures</span>}
                <span>elapsed {elapsed(drawer.started_at, drawer.completed_at)}</span>
              </div>

              <div className="space-y-1.5">
                {drawer.results.length === 0 && (
                  <div className="text-sm text-text-muted py-4 text-center border border-dashed border-white/10 rounded-lg">
                    No targets yet — engine still expanding the query.
                  </div>
                )}
                {drawer.results.map((r, i) => (
                  <div
                    key={`${r.target_id}-${i}`}
                    className={clsx(
                      'border rounded-lg px-3 py-2 text-xs flex items-start gap-2',
                      r.skipped
                        ? 'border-white/10 bg-white/[0.02] text-text-muted'
                        : r.ok
                          ? 'border-emerald-500/20 bg-emerald-500/[0.04]'
                          : 'border-rose-500/30 bg-rose-500/[0.05]',
                    )}
                  >
                    {r.skipped
                      ? <SkipForward className="w-4 h-4 mt-0.5 shrink-0" />
                      : r.ok
                        ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-300" />
                        : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-rose-300" />}
                    <div className="min-w-0 flex-1">
                      <div className="font-mono truncate">{r.target_id || '(no id)'}</div>
                      {r.detail && <div className="text-[11px] text-text-muted whitespace-pre-wrap break-all">{r.detail}</div>}
                      {r.status_code != null && <div className="text-[11px] text-text-muted">HTTP {r.status_code}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
